import React, { useEffect, useRef, useState } from 'react';
import { creditoAPI } from '@/lib/api';
import { Loader2, RefreshCw, CreditCard, AlertTriangle } from 'lucide-react';
import { CreditoBadge, situacao, AVISO_N2 } from './CreditoResultado';

const soDigitos = (v) => (v || '').replace(/\D/g, '');

// Checagem automática de crédito na Nova Venda — dispara quando CPF/CNPJ, nome, nascimento e CEP estão preenchidos
export const CreditoInline = ({ documento, tipoDocumento, nome, dataNascimento, cep, cidade, uf, categoria, modalidade, contrato, onResultado, testid = 'credito-inline' }) => {
  const doc = soDigitos(documento);
  const docValido = (tipoDocumento === 'CPF' && doc.length === 11) || (tipoDocumento === 'CNPJ' && doc.length === 14);
  const tipoVenda = modalidade === 'Base' ? 'BASE' : 'PROSPECT';
  const numeroContrato = tipoVenda === 'BASE' ? soDigitos(contrato) : '';
  const faltam = [];
  if ((nome || '').trim().length < 3) faltam.push('nome');
  if (tipoDocumento === 'CPF' && !dataNascimento) faltam.push('nascimento');
  if (soDigitos(cep).length !== 8) faltam.push('CEP');
  if (!cidade || (uf || '').length !== 2) faltam.push('cidade/UF');
  if (tipoVenda === 'BASE' && !numeroContrato) faltam.push('contrato da base');
  const pronto = docValido && faltam.length === 0;
  const payload = { cpf_cnpj: doc, nome: (nome || '').trim(), data_nascimento: dataNascimento || '', cep: soDigitos(cep), cidade: cidade || '', uf: (uf || '').toUpperCase(), tipo_venda: tipoVenda, numero_contrato: numeroContrato, plano_principal: 'TV', tipo_cliente: categoria === 'Empresarial' ? 'EMPRESARIAL' : 'RESIDENCIAL', origem: 'nova_venda' };
  const assinatura = pronto ? JSON.stringify(payload) : '';

  const [estado, setEstado] = useState({ assinatura: '', carregando: false, res: null });
  const cache = useRef({});
  useEffect(() => { onResultado?.(docValido && pronto && estado.res?.status === 'ok' ? estado.res : null); }, [estado.res, docValido, pronto]); // eslint-disable-line

  const consultar = async (forcar) => {
    setEstado({ assinatura, carregando: true, res: null });
    try {
      const res = (await creditoAPI.consultar({ ...payload, forcar })).data;
      cache.current[assinatura] = res;
      setEstado({ assinatura, carregando: false, res });
    } catch {
      setEstado({ assinatura, carregando: false, res: { status: 'erro', mensagem: 'Falha ao consultar o crédito' } });
    }
  };

  useEffect(() => {
    if (!assinatura) return undefined;
    if (cache.current[assinatura]) { setEstado({ assinatura, carregando: false, res: cache.current[assinatura] }); return undefined; }
    const t = setTimeout(() => consultar(false), 1500);
    return () => clearTimeout(t);
  }, [assinatura]); // eslint-disable-line

  if (!docValido) return null;
  if (!pronto) {
    return <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400" data-testid={testid}><CreditCard className="w-3 h-3" /> Crédito: preencha {faltam.join(', ')} para checar automaticamente</p>;
  }
  if (estado.carregando || estado.assinatura !== assinatura) {
    return <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400" data-testid={testid}><Loader2 className="w-3 h-3 animate-spin" /> Checando crédito na Claro…</p>;
  }
  const r = estado.res;
  if (!r || r.status !== 'ok') {
    const corpo = r?.resultado != null ? (typeof r.resultado === 'string' ? r.resultado : JSON.stringify(r.resultado)) : '';
    return (
      <div className="mt-1 space-y-0.5" data-testid={testid}>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-amber-600 dark:text-amber-400">
          <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Crédito não consultado{r?.mensagem ? `: ${r.mensagem}` : ''}</span>
          <button type="button" onClick={() => consultar(true)} className="inline-flex items-center gap-1 font-semibold text-purple-600 hover:underline" data-testid={`${testid}-tentar`}><RefreshCw className="w-3 h-3" /> Tentar de novo</button>
        </div>
        {corpo && <p className="text-[10px] font-mono text-gray-500 break-all" data-testid={`${testid}-detalhe`}>{corpo.slice(0, 300)}</p>}
      </div>
    );
  }
  const s = situacao(r.resultado, r.mensagem);
  return (
    <div className="mt-1.5 space-y-1" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <CreditoBadge resultado={r.resultado} mensagem={r.mensagem} testid={`${testid}-badge`} />
        {r.mensagem && <span className={`text-[11px] font-medium ${s.tom === 'verde' ? 'text-emerald-700 dark:text-emerald-300' : s.tom === 'vermelho' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-300'}`} data-testid={`${testid}-mensagem`}>{r.mensagem}</span>}
        {r.assinantes?.length > 0 && <span className="text-[11px] text-gray-500">{r.assinantes.length} contrato(s) na Claro</span>}
        <button type="button" onClick={() => consultar(true)} title="Consultar novamente" className="inline-flex items-center gap-1 text-[11px] text-purple-600 hover:underline" data-testid={`${testid}-novamente`}><RefreshCw className="w-3 h-3" /> {r.reaproveitada ? 'resultado recente — refazer' : 'refazer'}</button>
      </div>
      {s.n2 && (
        <p className="inline-flex items-start gap-1 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-1 text-[11px] font-semibold text-red-700 dark:text-red-300" data-testid={`${testid}-n2`}>
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {AVISO_N2} Se cadastrar, a venda entra como Cancelamento.
        </p>
      )}
    </div>
  );
};
